import { useEffect } from 'react'
import { FlagsProvider } from 'flagged'
import { Provider } from 'react-redux'
import { saveState } from 'lib/session-storage'
import { mergeUser } from 'registry/actions'
import { withContextualizedTracking } from 'lib/analytics'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import withReactQueryClientProvider from 'shared/HOC/withReactQueryClientProvider'
import { getOnboarding, getRegistry, getUser } from './reducers'

import store from './store'

import RegistryBuilderContainer from './registry-builder'

function getEventLocation() {
  if (typeof window !== 'undefined') {
    return new URLSearchParams(window.location.search).get('loc')
  }
}

interface FeatureFlags {
  [flag: string]: boolean
}

interface RegistryBuilderProps {
  featureFlags: FeatureFlags
  context: {
    authToken: string
    bookmarkletJS: string
    deviceSupportsBabylistApp: boolean
  }
}

function RegistryBuilder({ context, featureFlags }: RegistryBuilderProps) {
  const [currentUser] = useCurrentUser()

  // Persist redux state to sessionStorage so that user does not lose progress when refreshing the page.
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const state = store.getState()
      const user = getUser(state)
      const registry = getRegistry(state)
      const onboarding = getOnboarding(state)

      saveState({ onboarding, registry, user })
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    if (currentUser) {
      store.dispatch(mergeUser(currentUser))
    }
  }, [currentUser])

  return (
    <Provider store={store}>
      <FlagsProvider features={featureFlags}>
        <RegistryBuilderContainer context={context} />
      </FlagsProvider>
    </Provider>
  )
}

export default withContextualizedTracking({
  eventLocation: getEventLocation() || 'Registry Builder',
})(withReactQueryClientProvider(RegistryBuilder))
